"use client";

import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { timezoneStore } from "@/api/hooks/useConfig";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import { Granularity } from "@/components/pageCards/filterSort/periodicity";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useMemo, useState } from "react";

import moment from "moment";
import { FilterTimeFrameValue } from "server/config-api/index.js";

import DevelopmentInWasteTypesTenantGraph from "./graph";

type Props = {
  tenantId: string;
};

const DevelopmentInWasteTypesTenant: React.FC<Props> = ({ tenantId }) => {
  const { t } = useTrans();
  const { timezone } = timezoneStore();
  const { terminalConfig } = useConfigService();
  const { currentTerminal } = useTerminalsState();
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as ActiveOptions<any>);

  const defaultPeriod = useMemo(
    () =>
      terminalConfig?.defaultTimeFrame || FilterTimeFrameValue.Last6FullMonths,
    [terminalConfig],
  );

  const filters: FilterSort[] = useMemo(
    () => [
      createTimeFilters({ defaultValue: defaultPeriod }, [
        FilterTimeFrameValue.Last6FullMonths,
        FilterTimeFrameValue.LastFullYear,
      ]),
    ],
    [defaultPeriod],
  );

  const periodFilterValue = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const timeFrameWithCurrentMonth = useMemo(
    () => ({
      startTimeISO: periodFilterValue.startTimeISO,
      endTimeISO: moment().tz(timezone, true).toISOString(),
    }),
    [periodFilterValue, timezone],
  );

  const { data, isLoading: isCustomerPeriodsLoading } =
    trpc.dwh.customers.getPeriods.useQuery(
      {
        ...timeFrameWithCurrentMonth,
        terminalId: currentTerminal?.id || "",
        customerId: tenantId,
        granularity: Granularity.MONTH,
        timeZone: timezone,
      },
      {
        queryKeyHashFn: () =>
          [
            "customerPeriods",
            timeFrameWithCurrentMonth.startTimeISO,
            timeFrameWithCurrentMonth.endTimeISO,
            currentTerminal?.id,
            tenantId,
            timezone,
          ].toString(),
      },
    );

  const view = useMemo(() => {
    if (!data?.periods.find((p) => p.customer.totalOccurrences > 0)) {
      return (
        <div className="flex w-full flex-col justify-stretch">
          <EmptyView type="noDataForInterval" className="min-h-[300px]" />
        </div>
      );
    }

    return <DevelopmentInWasteTypesTenantGraph data={data} />;
  }, [data]);

  return (
    <Card
      title={t("circularityLabels:developmentInWasteTypes")}
      filters={filters}
      filterLocation={CardComponentFilterLocation.RIGHT}
      onFilterOptionChange={setActiveOptions}
      isLoading={isCustomerPeriodsLoading}
      className="min-w-[550px]"
    >
      {view}
    </Card>
  );
};

export default DevelopmentInWasteTypesTenant;
